import React from "react";
import { ReactGrid } from "@silevis/reactgrid";
import "@silevis/reactgrid/styles.css";
import "./tariffMatrixTable.css";
import {
  METADATA_COLUMN_IDS,
  buildTariffGridColumns,
  buildTariffGridRows,
  customCellTemplates,
  normalizeTariffColumnOrder,
  reorderTariffColumnIds,
  translateReactGridChanges,
} from "./reactGridAdapter";

const NOOP = () => {};
const EMPTY_SELECTION = new Set();

const TariffMatrixTable = ({
  rows = [],
  bands = [],
  loading = false,
  onPriceChange,
  onTargetRowChange,
  selectedRowIds = EMPTY_SELECTION,
  onSelectionChange = NOOP,
  onToggleActive = NOOP,
}) => {
  const bandColumnIds = React.useMemo(() => bands.map((band) => band.columnKey), [bands]);
  const rowLookupById = React.useMemo(
    () => new Map(rows.map((row) => [`row:${row.crossDockingPointId}`, row])),
    [rows]
  );
  const structuralRowIds = React.useMemo(
    () => rows.map((row) => `row:${row.crossDockingPointId}`),
    [rows]
  );
  const structuralColumnIds = React.useMemo(
    () => bands.map((band) => band.columnKey),
    [bands]
  );
  const validRowIds = React.useMemo(
    () => new Set(rows.map((row) => `row:${row.crossDockingPointId}`)),
    [rows]
  );
  const validColumnIds = React.useMemo(
    () => new Set(bands.map((band) => band.columnKey)),
    [bands]
  );

  const [lastFocusLocation, setLastFocusLocation] = React.useState(undefined);
  const [selectedRanges, setSelectedRanges] = React.useState([]);
  const [tariffColumnOrder, setTariffColumnOrder] = React.useState(bandColumnIds);
  const [columnWidthsById, setColumnWidthsById] = React.useState({});
  React.useEffect(() => {
    setTariffColumnOrder((current) => normalizeTariffColumnOrder(current, bandColumnIds));
  }, [bandColumnIds]);

  const columns = React.useMemo(
    () => buildTariffGridColumns(bands, { tariffColumnOrder, columnWidthsById }),
    [bands, tariffColumnOrder, columnWidthsById]
  );

  const selectionSet = React.useMemo(
    () => (selectedRowIds instanceof Set ? selectedRowIds : new Set(selectedRowIds || [])),
    [selectedRowIds]
  );

  const handleRowSelectionToggle = React.useCallback(
    (row, nextSelected) => {
      if (!row || row.crossDockingPointId == null) {
        return;
      }
      const next = new Set(selectionSet);
      if (nextSelected) {
        next.add(row.crossDockingPointId);
      } else {
        next.delete(row.crossDockingPointId);
      }
      onSelectionChange(next);
    },
    [onSelectionChange, selectionSet]
  );

  const handleHeaderSelectionToggle = React.useCallback(() => {
    const allSelected =
      rows.length > 0 && rows.every((row) => selectionSet.has(row.crossDockingPointId));
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(rows.map((row) => row.crossDockingPointId)));
    }
  }, [onSelectionChange, rows, selectionSet]);

  const handleToggleActive = React.useCallback(
    (row, nextActive) => {
      if (!row) {
        return;
      }
      onToggleActive(row, nextActive);
    },
    [onToggleActive]
  );

  const gridRows = React.useMemo(
    () =>
      buildTariffGridRows({
        rows,
        bands,
        tariffColumnOrder,
        selectedRowIds: selectionSet,
        onRowSelectionToggle: handleRowSelectionToggle,
        onHeaderSelectionToggle: handleHeaderSelectionToggle,
        onToggleActive: handleToggleActive,
      }),
    [
      bands,
      rows,
      tariffColumnOrder,
      selectionSet,
      handleRowSelectionToggle,
      handleHeaderSelectionToggle,
      handleToggleActive,
    ]
  );
  const gridInstanceKey = React.useMemo(
    () => `${structuralRowIds.join("|")}::${structuralColumnIds.join("|")}`,
    [structuralColumnIds, structuralRowIds]
  );
  const initialFocusLocation = React.useMemo(() => {
    if (!rows.length || !bands.length) {
      return undefined;
    }

    if (
      lastFocusLocation
      && validRowIds.has(lastFocusLocation.rowId)
      && validColumnIds.has(lastFocusLocation.columnId)
    ) {
      return lastFocusLocation;
    }

    return {
      rowId: `row:${rows[0].crossDockingPointId}`,
      columnId: bands[0].columnKey,
    };
  }, [bands, lastFocusLocation, rows, validColumnIds, validRowIds]);

  React.useEffect(() => {
    setSelectedRanges([]);
  }, [gridInstanceKey]);

  React.useEffect(() => {
    if (!lastFocusLocation || typeof onTargetRowChange !== "function") {
      return;
    }

    onTargetRowChange(rowLookupById.get(lastFocusLocation.rowId) || null);
  }, [lastFocusLocation, onTargetRowChange, rowLookupById]);

  const handleCellsChanged = React.useCallback((changes = []) => {
    const selectionChanges = changes.filter(
      (change) => change?.columnId === METADATA_COLUMN_IDS.selection
    );
    if (selectionChanges.length > 0) {
      const next = new Set(selectionSet);
      selectionChanges.forEach((change) => {
        const row = rowLookupById.get(change.rowId);
        if (!row) {
          return;
        }
        const key = row.crossDockingPointId;
        if (change.newCell?.checked) {
          next.add(key);
        } else {
          next.delete(key);
        }
      });
      onSelectionChange(next);
    }

    const rest = changes.filter(
      (change) => change?.columnId !== METADATA_COLUMN_IDS.selection
    );
    if (rest.length > 0) {
      translateReactGridChanges({
        changes: rest,
        rowLookupById,
        onPriceChange,
      });
    }
  }, [onPriceChange, onSelectionChange, rowLookupById, selectionSet]);

  const handleColumnResized = React.useCallback((columnId, width) => {
    setColumnWidthsById((current) => ({
      ...current,
      [columnId]: width,
    }));
  }, []);

  const handleColumnsReordered = React.useCallback((targetColumnId, columnIds, dropPosition) => {
    setTariffColumnOrder((current) => reorderTariffColumnIds(current, columnIds, targetColumnId, dropPosition));
  }, []);

  const canReorderColumns = React.useCallback((targetColumnId, columnIds) => (
    bandColumnIds.includes(targetColumnId)
    && columnIds.every((columnId) => bandColumnIds.includes(columnId))
  ), [bandColumnIds]);

  if (!bands.length) {
    return (
      <div className="cross-docking-sheet__empty">
        Тарифы не найдены
      </div>
    );
  }

  return (
    <div className="cross-docking-sheet">
      {loading ? (
        <div className="cross-docking-sheet__loading">
          Загрузка тарифов...
        </div>
      ) : null}

      <div
        className="cross-docking-sheet__grid"
        data-selection-count={selectedRanges.length}
      >
        <ReactGrid
          key={gridInstanceKey}
          columns={columns}
          rows={gridRows}
          customCellTemplates={customCellTemplates}
          initialFocusLocation={initialFocusLocation}
          onFocusLocationChanged={setLastFocusLocation}
          onSelectionChanged={setSelectedRanges}
          onCellsChanged={handleCellsChanged}
          onColumnResized={handleColumnResized}
          onColumnsReordered={handleColumnsReordered}
          canReorderColumns={canReorderColumns}
          stickyTopRows={1}
          stickyLeftColumns={5}
          enableRangeSelection
          enableFillHandle
          enableColumnSelection
          enableColumnResizeOnAllHeaders
          minColumnWidth={96}
          minRowHeight={36}
          moveRightOnEnter={false}
          horizontalStickyBreakpoint={80}
          zIndexBase={20}
        />
      </div>
    </div>
  );
};

export default TariffMatrixTable;
